// Map Search Scripts
import { MarkerClusterer } from '@googlemaps/markerclusterer';

(function() {
    // Colors: the theme's primary color is read from its CSS custom property, with a fallback if it isn't available
    const themeColor = (property, fallback) => getComputedStyle(document.documentElement).getPropertyValue(property).trim() || fallback;
    const primaryColorProperty = '--bs-primary';
    const primaryColorFallback = '#023047';
    const primaryColor = themeColor(primaryColorProperty, primaryColorFallback);
    // State outlines
    const outlineStrokeColor = '#ffffff';
    const outlineFillColor = primaryColor;
    const hoverOutlineStrokeColor = '#ffffff';
    const hoverOutlineFillColor = primaryColor;
    const activeOutlineStrokeColor = '#ffffff';
    const activeOutlineFillColor = primaryColor;
    // Location pin clusters
    const clusterStrokeColor = '#ffffff';
    const clusterTextColor = '#ffffff';

    // All state polygons
    let boundaries = [];
    // All markers
    let markerArray = [];
    // Clusters location pins that overlap at the current zoom
    // Set by initStatePolygons so resetMap() works when called without arguments
    let activeSearchMap = null;
    let activeConfig = null;
    let locationClusterer = null;
    // Location pins waiting to be clustered once the zoom animation finishes
    let pendingLocationMarkers = [];
    // State label markers (when market_display is 'label')
    let stateLabels = [];
    // Center map on Iowa by default (the only state they build in)
    let mapCenter = { lat:42.0046, lng:-93.2140 };
    // Default zoom level for map: Iowa fills a desktop-width map at 7, but is wider than a phone-width map
    const getDefaultZoom = () => {
        const mapDiv = document.getElementById('oimapsearch');
        return mapDiv && mapDiv.clientWidth && mapDiv.clientWidth < 600 ? 6 : 7;
    };
    // Button to reset search
    let resetButton = document.getElementById('btn-reset-map');
    let currentInfowindow;
    // Lifts an infowindow so its pointer sits on the top edge of a label instead of its center
    const getLabelOffset = () => new google.maps.Size(0, -33);
    // State labels are centered on their position (other labels sit above theirs by this much)
    const stateLabelPinSize = 20;
    const getStateLabelOffset = () => new google.maps.Size(0, -33 + stateLabelPinSize / 2);
    // Shared polygon options
    const polygonOptions = {
        strokeWeight: 1,
        strokeColor: outlineStrokeColor,
        fillColor: outlineFillColor,
        fillOpacity: 0.5,
    };
    const hoverOptions = {
        strokeWeight: 1,
        strokeColor: hoverOutlineStrokeColor,
        fillColor: hoverOutlineFillColor,
        fillOpacity: 0.45,
    };
    const activeOptions = {
        strokeWeight: 1,
        strokeColor: activeOutlineStrokeColor,
        fillColor: activeOutlineFillColor,
        fillOpacity: 0.125,
    };
    // Polygon events
    function setActiveOptions(event) {
        const polygon = this;
        polygon.setOptions(activeOptions);
    }
    function setHoverOptions(event) {
        const polygon = this;
        polygon.setOptions(hoverOptions);
    }
    function resetOptions(event) {
        const polygon = this;
        polygon.setOptions(polygonOptions);
    }
    // headerContent treats strings as plain text, so build a DOM element
    function buildHeader(text, href) {
        const heading = document.createElement('h3');
        heading.className = 'h6 mb-0';
        if (href) {
            const link = document.createElement('a');
                        link.href = href;
            link.textContent = text;
            heading.appendChild(link);
        } else {
            heading.textContent = text;
        }
        return heading;
    }
    // Total communities/homes for a state from the market + submarket data
    function getStateCounts(state, marketMapJSON) {
        let counts = { locations: 0, specs: 0 };
        for (let marketObj of marketMapJSON) {
            if (marketObj.state == state) {
                counts.locations += marketObj.locations || 0;
                counts.specs += marketObj.specs || 0;
            }
        }
        return counts;
    }
    // The marker's transparent pin image sits on top of its label, so the label never receives :hover
    // itself. Toggle the label's .hovered class from the marker's own mouse events instead.
    function watchLabelHover(marker) {
        const setHovered = (hovered) => {
            const label = marker.getLabel();
            if (label) {
                marker.setLabel({
                    ...label,
                    className: hovered ? 'market-marker-label hovered' : 'market-marker-label',
                });
            }
        };
        marker.addListener('mouseover', () => setHovered(true));
        marker.addListener('mouseout', () => setHovered(false));
    }
    // "N Communities / N Available Homes" popup for a market label. The label already shows the
    // name, so there is no heading unless a title is passed (state outlines have no label).
    function buildMarketInfoWindow(counts, link, onClose, title) {
        const wrapper = document.createElement('div');
        wrapper.className = 'market-label-iw-content';
        const communities = `<strong>${counts.locations}</strong> Communities`;
        const homes = `<strong>${counts.specs}</strong> Available Homes`;
        wrapper.innerHTML = `
            <button class="market-label-iw-close" type="button" aria-label="Close">&times;</button>
            <p class="mb-1">${link ? `<a href="${link}">${communities}</a>` : communities}</p>
            <p class="mb-0">${link ? `<a href="${link}available/">${homes}</a>` : homes}</p>
        `;
        if (title) {
            wrapper.prepend(buildHeader(title, link));
        }
        wrapper.querySelector('.market-label-iw-close').addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        });
        return wrapper;
    }
    // Scope the market infowindow styles to this window's container
    function styleMarketInfoWindow(infowindow, content) {
        google.maps.event.addListener(infowindow, 'domready', () => {
            const container = content.closest('.gm-style-iw-c');
            if (container) {
                container.classList.add('market-label-iw');
            }
        });
    }
    // How far down from the top of a state outline (as a fraction of its height) the infowindow's pointer sits,
    // so the window sits at the top of the state and overlaps it slightly
    const outlineInfowindowInset = .1;
    // Horizontally centered, near the top of the state
    function getOutlineInfowindowPosition(polygon) {
        const bounds = getPolygonBounds(polygon);
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        return {
            lat: ne.lat() - (ne.lat() - sw.lat()) * outlineInfowindowInset,
            lng: bounds.getCenter().lng(),
        };
    }
    function initStateInfowindow(event, searchMap, polygon, boundary, is_polygon, marketMapJSON, position) {
        if (currentInfowindow) {
            currentInfowindow.close();
        }
        let point;
        let counts = getStateCounts(polygon.name, marketMapJSON);
        // Labels already show the state name, outlines don't
        const content = buildMarketInfoWindow(counts, polygon.link, () => infowindow.close(), position ? null : polygon.full_name);
        let infowindow = new google.maps.InfoWindow({
            content: content,
            disableAutoPan: true,
        });
        styleMarketInfoWindow(infowindow, content);
        // Outlines open near the top of the state, labels from the label's position
        infowindow.setPosition(position || getOutlineInfowindowPosition(polygon));
        if (position) {
            infowindow.setOptions({ pixelOffset: getStateLabelOffset() });
        }
        infowindow.open(searchMap.map);
        polygon.addListener('click', (e) => {
            infowindow.close();
        });
        currentInfowindow = infowindow;
    }
    // Camera animation timing (ms): total pan time, and the delay between each zoom level
    const cameraPanDuration = 600;
    const cameraZoomStepDelay = 90;
    // Size (px) of the location pins
    const locationPinSize = { width: 27, height: 37 };
    // Zoom level when a state only has a single child item
    const singleMarkerZoom = 10;
    // Space (px) kept around the child items so their labels aren't cut off
    const childBoundsPadding = 80;
    // Longest wait (ms) for the map to go idle after a fitBounds() before moving on anyway
    const afterFitFallbackDelay = 1500;
    // fitBounds() can zoom in very tight when the bounds cover only a few close pins,
    // so cap the zoom after fitting. Users can still zoom in past it themselves.
    const maxAutoFitZoom = 14;
    // Every camera animation carries a job id so it stops if a newer animation, a reset,
    // or the user touching the map supersedes it.
    function startNewAnimationJob(map) {
        map.__oiAnimationJobId = (map.__oiAnimationJobId || 0) + 1;
        map.__oiAnimationDone = null;
        return map.__oiAnimationJobId;
    }
    function watchForUserInteraction(map) {
        if (map.__oiInteractionWatched) {
            return;
        }
        map.__oiInteractionWatched = true;
        // Dragging or using the zoom controls cancels the animation, but still runs its
        // completion callback so the clusters show up where the user left the map.
        map.getDiv().addEventListener('pointerdown', () => {
            const done = map.__oiAnimationDone;
            startNewAnimationJob(map);
            if (done) {
                done();
            }
        }, { capture: true });
    }
    // panTo() only animates for moves smaller than a viewport, so interpolate the pan manually
    function smoothPanTo(map, target, jobId, onDone, duration = cameraPanDuration) {
        const start = map.getCenter();
        if (!start) {
            map.setCenter(target);
            onDone();
            return;
        }
        const startLat = start.lat();
        const startLng = start.lng();
        const endLat = target.lat();
        const endLng = target.lng();
        const startTime = performance.now();
        function step(now) {
            if (map.__oiAnimationJobId !== jobId) {
                return;
            }
            const t = Math.min(1, (now - startTime) / duration);
            map.setCenter({
                lat: startLat + (endLat - startLat) * t,
                lng: startLng + (endLng - startLng) * t,
            });
            if (t < 1) {
                requestAnimationFrame(step);
            } else {
                onDone();
            }
        }
        requestAnimationFrame(step);
    }
    // No native animated zoom, so step one level at a time on a plain timer
    function smoothZoomTo(map, targetZoom, jobId, onDone, stepDelay = cameraZoomStepDelay) {
        if (map.__oiAnimationJobId !== jobId) {
            return;
        }
        const current = map.getZoom();
        if (current === targetZoom) {
            onDone();
            return;
        }
        map.setZoom(current + (targetZoom > current ? 1 : -1));
        setTimeout(() => smoothZoomTo(map, targetZoom, jobId, onDone, stepDelay), stepDelay);
    }
    // Pan + zoom to a target, then run onDone once both have finished (or the user took over)
    function animateCamera(map, center, zoom, onDone = () => {}) {
        watchForUserInteraction(map);
        const jobId = startNewAnimationJob(map);
        let remaining = 2;
        let finished = false;
        const finish = () => {
            if (finished) {
                return;
            }
            finished = true;
            map.__oiAnimationDone = null;
            onDone();
        };
        const partDone = () => {
            remaining -= 1;
            if (remaining === 0) {
                finish();
            }
        };
        map.__oiAnimationDone = finish;
        smoothPanTo(map, center, jobId, partDone);
        smoothZoomTo(map, zoom, jobId, partDone);
    }
    function getPolygonBounds(polygon) {
        var bounds = new google.maps.LatLngBounds();
        var paths = polygon.getPaths();
        var path;
        for (var i = 0; i < paths.getLength(); i++) {
            path = paths.getAt(i);
            for (var ii = 0; ii < path.getLength(); ii++) {
                bounds.extend(path.getAt(ii));
            }
        }
        return bounds;
    }
    function clickPolygon(event, stateBorder, searchMap, config) {
        const polygon = stateBorder;
        polygon.setOptions(activeOptions);
        google.maps.event.clearListeners(stateBorder, 'mouseover');
        polygon.addListener('mouseover', setActiveOptions);
        polygon.addListener('mouseout', setActiveOptions);
        let statesToDraw = [polygon.name];
        updateStatePolygons(event, searchMap, statesToDraw, config.marketDisplay != 'label');
        markerArray = [];
        const marketTypes = [];
        if (config.showSubmarkets) {
            marketTypes.push('submarket');
        }
        if (config.showCounties) {
            marketTypes.push('county');
        }
        if (config.showCities) {
            marketTypes.push('city');
        }
        if (marketTypes.length) {
            addMarkers(event, polygon.name, searchMap, config.marketMapJSON, config.pinSVG, marketTypes);
        }
        if (config.showLocations) {
            addLocationMarkers(polygon.name, searchMap, config.locationMapJSON, config.locationPinSVG);
        }
        fitToMarkers(searchMap, polygon);
        updateResetButton(resetButton, true, stateBorder.full_name);
        let map = document.getElementById('oimapsearch');
        let pointerEventsInitial = map.style.pointerEvents;
        map.style.pointerEvents = 'none';
        setTimeout(() => {
            map.style.pointerEvents = pointerEventsInitial;
        }, 1000);
    }
    // Fit the selected state in the window, then once that has settled fit the map around its child items
    // (submarkets, cities, counties, locations). With no child items it stops at the state.
    function fitToMarkers(searchMap, polygon) {
        const map = searchMap.map;
        startNewAnimationJob(map); // cancel any zoom animation in progress

        // Runs once the map is idle after a fitBounds(), or after a short wait if the view didn't change
        const afterFit = (callback) => {
            let called = false;
            const run = () => {
                if (!called) {
                    called = true;
                    callback();
                }
            };
            google.maps.event.addListenerOnce(map, 'idle', run);
            setTimeout(run, afterFitFallbackDelay);
        };

        const fitChildren = () => {
            if (markerArray.length == 1) {
                map.panTo(markerArray[0].getPosition());
                map.setZoom(singleMarkerZoom);
            } else {
                const bounds = new google.maps.LatLngBounds();
                for (let marker of markerArray) {
                    bounds.extend(marker.getPosition());
                }
                map.fitBounds(bounds, childBoundsPadding);
            }
            // Location pins group into clusters once the final zoom has finished
            afterFit(() => {
                if (map.getZoom() > maxAutoFitZoom) {
                    map.setZoom(maxAutoFitZoom);
                }
                showLocationClusters(searchMap);
            });
        };

        map.fitBounds(getPolygonBounds(polygon));
        if (markerArray.length) {
            afterFit(fitChildren);
        }
    }
    function addMarkers(event, state, searchMap, marketMapJSON, pinSVG, marketTypes) {
        for (let marketObj of marketMapJSON) {
            if (marketObj.state == state && marketTypes.includes(marketObj.market_type)) {
                // Get lat/lng position
                let postition = {
                    lat: parseFloat(marketObj.latitude),
                    lng: parseFloat(marketObj.longitude),
                };
                let marker = new google.maps.Marker({
                    position: postition,
                    map: searchMap.map,
                    icon: {
                        url: pinSVG,
                        size: new google.maps.Size(256, 48),
                    },
                    title: marketObj.market_name,
                    link: marketObj.permalink,
                    locations: marketObj.locations,
                    specs: marketObj.specs,
                    label: {
                        text: marketObj.market_name,
                        className: 'market-marker-label'
                    },
                });
                watchLabelHover(marker);
                const content = buildMarketInfoWindow(
                    { locations: marker.locations, specs: marker.specs },
                    marker.link,
                    () => infowindow.close()
                );
                const infowindow = new google.maps.InfoWindow({
                    content: content,
                    ariaLabel: marketObj.market_name,
                    disableAutoPan: true,
                });
                styleMarketInfoWindow(infowindow, content);
                marker.addListener('mouseover', () => {
                    if (currentInfowindow) {
                        currentInfowindow.close();
                    }
                    infowindow.setPosition(marker.getPosition());
                    infowindow.setOptions({ pixelOffset: getLabelOffset() });
                    infowindow.open(searchMap.map);
                    currentInfowindow = infowindow;
                });

                marker.addListener('click', () => {
                    window.location.href = marker.link;
                    infowindow.close();
                });
                if (!markerArray.includes(marker)) {
                    markerArray.push(marker);
                }
            }
        }
    }
    function addLocationMarkers(state, searchMap, locationMapJSON, locationPin) {
        const locationMarkers = [];
        for (let locationObj of locationMapJSON) {
            if (locationObj.state != state) {
                continue;
            }
            const template = document.querySelector(`#search-map-location-infowindows template[data-location-id="${locationObj.location_id}"]`);
            if (!template) {
                continue;
            }
            const fragment = template.content.cloneNode(true);
            const header = fragment.querySelector('.oi-infowindow-header');
            const content = fragment.querySelector('.oi-infowindow');
            if (!header || !content) {
                continue;
            }
            const marker = new google.maps.Marker({
                position: {
                    lat: parseFloat(locationObj.latitude),
                    lng: parseFloat(locationObj.longitude),
                },
                map: searchMap.map,
                icon: {
                    url: locationPin,
                    scaledSize: new google.maps.Size(locationPinSize.width, locationPinSize.height),
                },
            });
            const infowindow = new google.maps.InfoWindow({
                headerContent: header,
                content: content,
            });
            marker.addListener('mouseover', () => {
                if (currentInfowindow) {
                    currentInfowindow.close();
                }
                infowindow.open({
                    anchor: marker,
                    map: searchMap.map,
                });
                currentInfowindow = infowindow;
            });
            markerArray.push(marker);
            locationMarkers.push(marker);
        }
        pendingLocationMarkers = locationMarkers;
    }
    function showLocationClusters(searchMap) {
        if (!pendingLocationMarkers.length || locationClusterer) {
            return;
        }
        locationClusterer = new MarkerClusterer({
            map: searchMap.map,
            markers: pendingLocationMarkers,
            renderer: { render: renderCluster },
        });
    }
    // Circle with the number of pins, styled to match the theme
    function renderCluster({ count, position }) {
        const color = primaryColor;
        const size = count < 10 ? 36 : count < 100 ? 44 : 52;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="${clusterStrokeColor}" stroke-width="2"/></svg>`;
        return new google.maps.Marker({
            position,
            icon: {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                scaledSize: new google.maps.Size(size, size),
                anchor: new google.maps.Point(size / 2, size / 2),
            },
            label: {
                text: String(count),
                color: clusterTextColor,
                fontSize: '14px',
                fontWeight: '600',
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
    }
    function updateStatePolygons(event, searchMap, statesToDraw, showOutline = true) {
        // State labels are only shown before a state is selected
        for (let label of stateLabels) {
            label.setMap(statesToDraw.length ? null : searchMap.map);
        }
        for (let boundary of boundaries) {
            if (statesToDraw.includes(boundary.state)) {
                boundary.border.setMap(showOutline ? searchMap.map : null);
            } else {
                boundary.border.setMap(null);
            }
        }
    }
    function updateResetButton(button, active=false, text) {
        if (active) {
            button.classList.add('active');
        }
        else {
            button.classList.remove('active');
        }
        if (text) {
            button.innerHTML = `<i class="fa-sharp fa-regular fa-undo"></i> ${text}`;
        }
    }

    window.initStatePolygons = function(event, searchMap, config, animate = false) {
        activeSearchMap = searchMap;
        activeConfig = config;
        let index = 0;
        // Add polygon for each state
        for (let stateObj of config.stateJSON) {
            if (stateObj.google_boundary && Array.isArray(stateObj.google_boundary)) {
                let stateBorder = new google.maps.Polygon({
                    paths: stateObj.google_boundary,
                    name: stateObj.state,
                    options: polygonOptions,
                    full_name: stateObj.state_name,
                    link: stateObj.permalink,
                });
                boundaries.push({
                    state: stateObj.state,
                    index: index,
                    border: stateBorder,
                });
                if (config.marketDisplay == 'label') {
                    // Show a label at the center of the state instead of the outline
                    let center = getPolygonBounds(stateBorder).getCenter();
                    let label = new google.maps.Marker({
                        position: center,
                        map: searchMap.map,
                        // Anchor the (invisible) pin image by its center so the label sits on the state's center
                        icon: {
                            url: config.pinSVG,
                            scaledSize: new google.maps.Size(stateLabelPinSize, stateLabelPinSize),
                            anchor: new google.maps.Point(stateLabelPinSize / 2, stateLabelPinSize / 2),
                            labelOrigin: new google.maps.Point(stateLabelPinSize / 2, stateLabelPinSize / 2),
                        },
                        title: stateObj.state_name,
                        label: {
                            text: stateObj.state_name,
                            className: 'market-marker-label'
                        },
                    });
                    watchLabelHover(label);
                    label.addListener('mouseover', (event) => {
                        initStateInfowindow(event, searchMap, stateBorder, stateObj.google_boundary, stateObj.is_polygon, config.marketMapJSON, center);
                    });
                    label.addListener('click', (e) => {
                        if (currentInfowindow) {
                            currentInfowindow.close();
                        }
                        clickPolygon(e, stateBorder, searchMap, config);
                    });
                    stateLabels.push(label);
                } else {
                    stateBorder.setMap(searchMap.map);
                }
                // Add initial event listeners for this polygon
                stateBorder.addListener('mouseover', setHoverOptions);
                stateBorder.addListener('mouseover', (event) => {
                    initStateInfowindow(event, searchMap, stateBorder, stateObj.google_boundary, stateObj.is_polygon, config.marketMapJSON);
                });
                stateBorder.addListener('mouseout', resetOptions);
                stateBorder.addListener('click', (e) => {
                    clickPolygon(e, stateBorder, searchMap, config);
                });
                index += 1;
            }
        }
        if (animate) {
            animateCamera(searchMap.map, new google.maps.LatLng(mapCenter), getDefaultZoom());
        } else {
            startNewAnimationJob(searchMap.map); // cancel any zoom animation in progress
            searchMap.map.setCenter(mapCenter);
            searchMap.map.setZoom(getDefaultZoom());
        }
    };
    window.resetMap = function(event, searchMap = activeSearchMap, config = activeConfig, animate = true) {
        // The modal's onclick calls this without arguments
        if (!searchMap) {
            return;
        }
        if (currentInfowindow) {
            currentInfowindow.close();
        }
        updateResetButton(resetButton);
        updateStatePolygons(event, searchMap, []);
        boundaries = [];
        for (let label of stateLabels) {
            label.setMap(null);
        }
        stateLabels = [];
        startNewAnimationJob(searchMap.map);
        pendingLocationMarkers = [];
        if (locationClusterer) {
            locationClusterer.clearMarkers();
            locationClusterer.setMap(null);
            locationClusterer = null;
        }
        for (let i = 0; i < markerArray.length; i++) {
            markerArray[i].setMap(null);
        }
        markerArray = [];
        if (config && config.stateJSON) {
            initStatePolygons(event, searchMap, config, animate);
        }
    };

    // Initialize the search map from config passed in by search_map.html
    function initSearchMap(config) {
        window.addEventListener('oilib_loaded', () => {
            new oi.map.multi('oimapsearch', {
                listing_type: 'search',
                latitude: '42.0046',
                longitude: '-93.2140',
                zoom_control_position:'right-bottom',
                styles: config.mapStyles,
                zoom: 7,
                nearby: true,
                loading: 'lazy',
            });
        });

        window.addEventListener('oimap_idle', (event) => {
            if (event.detail.map_id == 'oimapsearch') {
                const searchMap = oi.loaded.map.oimapsearch;
                if (config.stateJSON) {
                    resetButton.addEventListener('click', (e) => {
                        resetMap(e, searchMap, config);
                    });
                }
                initStatePolygons(event, searchMap, config);

                // Always reopen the map modal in its original state
                const mapModal = document.getElementById('search-map-modal');
                if (mapModal) {
                    mapModal.addEventListener('hidden.bs.modal', () => {
                        if (resetButton.classList.contains('active')) {
                            resetMap(null, searchMap, config, false);
                        }
                    });
                }
            }
        });
    }
    if (window.searchMapConfig) {
        initSearchMap(window.searchMapConfig);
    }

    window.anchorHomeMap = function() {
        let myModalEl = document.getElementById('search-modal');
        let myNav = document.getElementById('main-nav');
        let scrollToDiv = document.getElementById('map-search');

        let searchModal = bootstrap.Modal.getInstance(myModalEl)
        let mobileNav = bootstrap.Offcanvas.getInstance(myNav)

        if(searchModal != null){
            searchModal.hide();
        }

        if(mobileNav != null){
            mobileNav.hide();
        }

       if(scrollToDiv != null){
            scrollToDiv.scrollIntoView({
                behavior: 'smooth'
            });
        }
    }
})();
